import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseDeleteMutation } from "@/v2-backoffice-client/license/delete/license.delete.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseDeleteMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Removes a license created by mistake. Ending a trial that ran its course is
// LicenseEndMenuItem (keeps the row as history); this removes the row from
// the board entirely. The temporal history table still holds what was
// deleted and by whom.
function LicenseDeleteMenuItem(props: LicenseDeleteMenuItemProps) {
  const { license, ...menuItemProps } = props

  const [deleteLicense, { loading, error }] = useLicenseDeleteMutation({
    update: evictLicenseQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "License deleted",
        description: `${license.CustomerName ?? "The customer"}'s ${license.ProductName} license was removed.`,
      })
    },
  })

  return (
    <ActionMenu.ConfirmItem
      iconSrc="trash"
      variant="destructive"
      isLoading={loading}
      error={error}
      errorTitle="Failed to delete license"
      confirmTitle="Delete License"
      confirmDescription={`Are you sure you want to delete ${license.CustomerName ?? "this customer"}'s ${license.ProductName} license? Use this only for rows created by mistake — a trial that ran its course should be ended instead, so it stays on the board as history.`}
      confirmText="Delete"
      confirmVariant="destructive"
      data-feature-code="UI_BackofficeLicense_Row_Delete"
      onConfirm={async () => {
        await deleteLicense({ variables: { id: license.Id } })
      }}
      {...menuItemProps}
    >
      Delete
    </ActionMenu.ConfirmItem>
  )
}

export default LicenseDeleteMenuItem
